import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronRight, CheckCircle2, TrendingUp, Shield, Target, Award, Users, Briefcase } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const AmazonServices = () => {
  const whyChooseUs = [
    'Experienced Amazon Account Managers',
    'Deep Understanding of Amazon Policies & Algorithms',
    'Data-Driven, Result-Oriented Strategy',
    'Customized Growth Plans',
    'Long-Term Scalable Approach'
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Amazon Account Management Services India | Obiquiste Solutions</title>
        <meta name="description" content="Complete Amazon seller account management: listing optimization, PPC ads, account health, inventory & A+ content. Boost your Amazon sales with experts." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/amazon-account-management-services" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/services" className="text-slate-700 hover:text-primary font-medium">Services</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">Amazon Account Management</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="py-16 md:py-24 lg:py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.03) 0%, rgba(255, 193, 7, 0.03) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight mb-6">
                Scale. Optimize. <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">Dominate Amazon.</span>
              </h1>
              <p className="text-xl md:text-2xl font-semibold text-slate-800 mb-4">
                At Obiquiste Solutions, we don't just manage Amazon accounts — we build scalable, profitable brands.
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8">
                From product listings to advertising optimization, account health to brand protection — we provide complete end-to-end Amazon account management designed to increase sales, protect your account, and maximize ROI.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?service=amazon">
                  <Button className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl px-8 py-6 rounded-full text-base font-semibold">
                    Request Free Amazon Audit
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact?service=amazon">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold">
                    Talk to Amazon Expert
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-400/20 to-yellow-400/20 rounded-3xl blur-2xl"></div>
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/216a6a1a-ff3d-4c72-88de-4c9fd30cd839/images/0ea5b94682a282ada84bca848c00147c95036daecb1b1f8121c3ab50fc13e007.png"
                  alt="Amazon Analytics Dashboard"
                  className="relative rounded-2xl shadow-2xl"
                />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 1. Product Listing Management */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6 text-center">
              Product Listing Management
            </h2>
            <p className="text-xl text-slate-700 mb-8 text-center">
              We create high-converting, fully optimized listings inside Seller Central.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Complete listing creation using your product data',
                'SEO-optimized titles, bullet points & backend keywords',
                '100 SKUs execution within 30 days',
                'Structured catalog management for scalable growth'
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Pricing & Inventory Control */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4 text-center">
              Pricing & Inventory Control
            </h2>
            <p className="text-xl text-slate-700 mb-8 text-center">
              Stay competitive. Stay in stock.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                'Real-time price updates',
                'Inventory tracking & stock optimization',
                'Strategic pricing support to protect margins'
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-3 p-6 bg-white rounded-xl shadow-sm border border-slate-200">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <span className="text-slate-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3. Sponsored Ads Management (PPC) */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6 text-center">
              Sponsored Ads Management (PPC)
            </h2>
            <p className="text-xl text-slate-700 mb-8 text-center">
              Turn ad spend into profitable revenue.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                'Campaign creation & restructuring',
                'Keyword research & targeting strategy',
                'Bid optimization & ACoS control',
                'Continuous scaling of winning campaigns'
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 p-6 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl border-l-4 border-primary">
              <p className="text-lg font-semibold text-slate-900 text-center">
                Focus: Lower ACoS. Higher ROI. Sustainable growth.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4. Account Health & Compliance Management */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4 text-center">
              Account Health & Compliance Management
            </h2>
            <p className="text-xl text-slate-700 mb-8 text-center">
              Your account safety is our priority.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'A-to-Z claim handling',
                'Negative feedback monitoring & removal requests',
                'Policy compliance management',
                'POA (Plan of Action) support for suspension issues (Add-on Service)'
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-3 p-4 bg-white rounded-lg shadow-sm">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 5. FBA & Operational Support */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4 text-center">
              FBA & Operational Support
            </h2>
            <p className="text-xl text-slate-700 mb-8 text-center">
              Smooth backend operations = uninterrupted sales.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                'FBA shipment planning & creation',
                'Pickup coordination & tracking',
                'Reimbursement claims for lost/damaged inventory'
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-3 p-6 bg-slate-50 rounded-xl border border-slate-200">
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <span className="text-slate-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6. Deals, Promotions & Growth Boosters + 7. Buyer Communication + 8. Brand Registry */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* 6. Deals & Promotions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              className="bg-white p-8 rounded-2xl border border-slate-200"
            >
              <h3 className="font-heading text-2xl font-bold mb-4">Deals, Promotions & Growth Boosters</h3>
              <p className="text-slate-600 mb-6">Increase traffic. Improve conversions.</p>
              <div className="space-y-3">
                {[
                  'Coupons & Lightning Deals setup',
                  'Promotional campaign management',
                  'Strategic discount planning'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* 7. Buyer Communication */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8 rounded-2xl border border-slate-200"
            >
              <h3 className="font-heading text-2xl font-bold mb-4">Buyer Communication & Review Growth</h3>
              <p className="text-slate-600 mb-6">Customer experience drives rankings.</p>
              <div className="space-y-3">
                {[
                  '24-hour buyer-seller messaging response',
                  'Structured review enhancement strategy',
                  'Feedback monitoring & management'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* 8. Brand Registry */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-8 rounded-2xl border border-slate-200"
            >
              <h3 className="font-heading text-2xl font-bold mb-4">Brand Registry & Infringement Protection</h3>
              <p className="text-slate-600 mb-6">Protect what you've built.</p>
              <div className="space-y-3">
                {[
                  'Brand Registry documentation support',
                  'Infringement report filing',
                  'Unauthorized seller monitoring'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Obiquiste Solutions */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Why Choose Obiquiste Solutions
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whyChooseUs.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-center space-x-3 p-5 bg-slate-50 rounded-xl border border-slate-200"
              >
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                <span className="text-slate-800 font-medium">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands We Work With */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Award className="w-4 h-4 mr-2" />
              Our Clients
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Amazon Brands We Currently Work With
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              We are proud to manage Amazon accounts for 20+ brands across diverse categories, helping them achieve sustainable growth.
            </p>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Brand</th>
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Product / Focus</th>
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Category</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { brand: 'Nutri Hut', product: 'FMCG / Nutrition Products', category: 'FMCG / Food & Nutrition' },
                  { brand: 'Rayon Caboki', product: 'Hair/Beauty Products', category: 'Beauty & Personal Care / Hair Care' },
                  { brand: 'Ayurdaanta Herbs', product: 'Ayurvedic Herbs & Products', category: 'Health & Wellness / Ayurveda' },
                  { brand: 'Ayur Devam', product: 'Ayurvedic/FMCG Products', category: 'Health & Wellness / Ayurveda' },
                  { brand: 'Suparna Trikha', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                  { brand: 'Haute', product: 'Gas Stoves', category: 'Home & Kitchen / Kitchen Appliances' },
                  { brand: 'GK Trading', product: 'Wallpapers', category: 'Home Improvement / Home Décor' },
                  { brand: 'Yuri Woori', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                  { brand: 'Mooncntrl', product: 'Apparel', category: 'Fashion / Apparel' },
                  { brand: 'Indian Kitchen Foods', product: 'Ready-to-Eat Meals', category: 'FMCG / Packaged Food & Ready-to-Eat' },
                  { brand: 'Earthvya', product: 'Supplements', category: 'Health & Wellness / Nutraceuticals' },
                  { brand: 'GW Khamkar', product: 'FMCG Products', category: 'FMCG / Food & Grocery' },
                  { brand: 'Vintage Oils', product: 'Oils / Edible Products', category: 'FMCG / Food & Grocery' },
                  { brand: 'Fitelo', product: 'Smart Weighing Scales', category: 'Electronics / Smart Health Devices' },
                  { brand: 'Fitty', product: 'Supplements', category: 'Health & Wellness / Nutraceuticals' },
                  { brand: 'RajGharana Metals', product: 'Kitchen Utensils', category: 'Home & Kitchen / Cookware & Kitchenware' },
                  { brand: 'HII Health Inside It', product: 'Health & Wellness Products', category: 'Health & Wellness' },
                  { brand: 'Auli Lifestyle', product: 'Skincare & Beauty Products', category: 'Beauty & Personal Care / Skincare' },
                  { brand: 'EHOP', product: 'TV & Electronic Remotes', category: 'Electronics / Consumer Electronics Accessories' },
                  { brand: 'Lavenust', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                ].map((item, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-slate-100 hover:bg-orange-50/50 transition-colors"
                  >
                    <td className="py-4 px-6">
                      <span className="font-semibold text-slate-800">{item.brand}</span>
                    </td>
                    <td className="py-4 px-6 text-slate-600">{item.product}</td>
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                        {item.category}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="max-w-4xl mx-auto text-center"
          >
            <Briefcase className="w-16 h-16 mx-auto mb-6 text-primary" />
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Our Mission
            </h2>
            <p className="text-xl md:text-2xl leading-relaxed text-slate-300">
              To transform Amazon sellers into powerful, profitable brands through strategic execution, operational excellence, and performance marketing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-12 border border-orange-200">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Let's Scale Your Amazon Business
            </h2>
            <p className="text-lg md:text-xl text-slate-700 mb-8">
              We look forward to partnering with you to build a structured, profitable, and scalable Amazon growth system.
            </p>
            <Link to="/contact?service=amazon">
              <Button className="bg-primary text-white hover:bg-primary-600 px-10 py-7 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl">
                Get Your Free Amazon Growth Audit
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AmazonServices;
